import { Box, Flex, Stack, Text } from '@chakra-ui/react';
import { Sparks } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { websiteDomain } from '../../brand/website-url';
import { ONBOARDING_PALETTE } from '../onboarding-palette';
import type { OnboardingSnapshot } from '../onboarding-snapshot';
import { PanelCard } from './PanelCard';
import type { PanelCardState } from './panel-state';

const PlaceholderBars = ({ pulsing }: { pulsing: boolean }) => (
  <Stack gap="6px" aria-hidden>
    {['100%', '86%', '62%'].map((width) => (
      <Box
        key={width}
        h="8px"
        w={width}
        borderRadius="full"
        bg="border.subtle"
        animation={pulsing ? 'pulse 1.6s ease-in-out infinite' : undefined}
      />
    ))}
  </Stack>
);

const DnaBody = ({ snapshot }: { snapshot: OnboardingSnapshot }) => {
  const { t } = useTranslation();

  if (snapshot.dnaStatus === 'ready' && snapshot.dnaText) {
    return (
      <Box bg="lilac.100" borderRadius="6px" px="8px" py="6px">
        <Text fontSize="12.5px" lineHeight="1.45" color="lilac.700" lineClamp={3}>
          {snapshot.dnaText}
        </Text>
      </Box>
    );
  }

  if (snapshot.dnaStatus === 'retry') {
    return (
      <Text fontSize="12px" color="inkMuted">
        {t('onboarding.brandPanel.brand.dnaRetry')}
      </Text>
    );
  }

  const isLoading = snapshot.dnaStatus === 'loading';
  return (
    <Stack gap="8px">
      <PlaceholderBars pulsing={isLoading} />
      <Text fontSize="12px" color="inkSubtle">
        {t(
          isLoading
            ? 'onboarding.brandPanel.brand.dnaLoading'
            : 'onboarding.brandPanel.brand.dnaEmpty'
        )}
      </Text>
    </Stack>
  );
};

/** Name, domain and Brand DNA of the brand, filled in as step 1 is typed. */
export const BrandPanelCard = ({
  snapshot,
  state,
}: {
  snapshot: OnboardingSnapshot;
  state: PanelCardState;
}) => {
  const { t } = useTranslation();
  const name = snapshot.brandName.trim();
  const domain = websiteDomain(snapshot.websiteUrl);

  return (
    <PanelCard state={state}>
      <Flex align="center" gap="12px">
        <Flex
          flex="none"
          w="42px"
          h="42px"
          borderRadius="11px"
          bg={ONBOARDING_PALETTE.initialTile}
          align="center"
          justify="center"
          fontFamily="heading"
          fontSize="17px"
          fontWeight="700"
          color="ink"
          aria-hidden
        >
          {(name || t('onboarding.brandPanel.brand.namePlaceholder')).charAt(0).toUpperCase()}
        </Flex>
        <Box minW={0}>
          <Text fontFamily="heading" fontSize="16px" fontWeight="600" color="ink" truncate>
            {name || t('onboarding.brandPanel.brand.namePlaceholder')}
          </Text>
          <Text fontSize="12.5px" color="inkMuted" truncate>
            {domain ?? t('onboarding.brandPanel.brand.websitePlaceholder')}
          </Text>
        </Box>
      </Flex>

      <Box h="1px" bg="border.subtle" my="12px" />

      <Flex align="center" gap="6px" mb="8px">
        <Sparks width={13} height={13} color="var(--chakra-colors-lilac-700)" aria-hidden />
        <Text fontFamily="heading" fontSize="11.5px" fontWeight="600" color="lilac.700">
          {t('onboarding.brandPanel.brand.dnaLabel')}
        </Text>
      </Flex>
      <DnaBody snapshot={snapshot} />
    </PanelCard>
  );
};
